import React, { useEffect, useState } from "react";
import api from "../../api/api";
import { useAcademicYear } from "../../AcademicYearContext";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Grid,
  Autocomplete,
} from "@mui/material";
import { toast } from "react-toastify";

const periods = [
  "Period 1",
  "Period 2",
  "Period 3",
  "Exam",
  "Period 4",
  "Period 5",
  "Period 6",
  "Exam2",
];

const BulkTestScorePage = () => {
  const { selectedYear } = useAcademicYear();

  const [gradeClasses, setGradeClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);

  const [selectedGradeClass, setSelectedGradeClass] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  const [testScores, setTestScores] = useState(
    periods.reduce((acc, p) => ({ ...acc, [p]: "" }), {})
  );

  const [loading, setLoading] = useState(false);

  /* =========================
     LOAD GRADE CLASSES
  ========================= */
  useEffect(() => {
    api
      .get("/api/users/grade-classes/")
      .then((res) => setGradeClasses(res.data))
      .catch((err) => console.error("Grade class error:", err));
  }, []);

  /* =========================
     LOAD STUDENTS BY CLASS + ACTIVE YEAR
  ========================= */
  useEffect(() => {
    if (selectedGradeClass && selectedYear?.id) {
      api
        .get(
          `/students-in-class/${selectedGradeClass.id}/?academic_year=${selectedYear.id}`
        )
        .then((res) => setStudents(res.data))
        .catch((err) =>
          console.error("Student fetch error:", err.response?.data || err)
        );
    } else {
      setStudents([]);
    }
  }, [selectedGradeClass, selectedYear]);

  /* =========================
     LOAD SUBJECTS
  ========================= */
  useEffect(() => {
    api
      .get("/api/users/subjects/")
      .then((res) => setSubjects(res.data))
      .catch((err) => console.error("Subject error:", err));
  }, []);

  /* =========================
     HANDLE SCORE INPUT
  ========================= */
  const handleScoreChange = (period, value) => {
    setTestScores((prev) => ({
      ...prev,
      [period]: value,
    }));
  };

  /* =========================
     SUBMIT WITH 3s SPINNER
  ========================= */
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!selectedGradeClass || !selectedStudent || !selectedSubject) {
      toast.error("Please select grade class, student, and subject!");
      return;
    }

    setLoading(true);

    setTimeout(async () => {
      const payload = {
        grade_class: selectedGradeClass.id,
        student: selectedStudent.id,
        subject: selectedSubject.id,
        period_1: testScores["Period 1"],
        period_2: testScores["Period 2"],
        period_3: testScores["Period 3"],
        exam: testScores["Exam"],
        period_4: testScores["Period 4"],
        period_5: testScores["Period 5"],
        period_6: testScores["Period 6"],
        exam2: testScores["Exam2"],
      };

      try {
        await api.post(
          "/api/users/grades-distribution/bulk-test-score/",
          payload
        );

        toast.success("All test scores saved successfully!");

        // Reset only scores
        setTestScores(
          periods.reduce((acc, p) => ({ ...acc, [p]: "" }), {})
        );
      } catch (err) {
        toast.error(
          err.response?.data?.error || "Something went wrong!"
        );
      } finally {
        setLoading(false);
      }
    }, 3000);
  };

  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", mt: 5, p: 3 }}>
      <Typography variant="h4" textAlign="center" mb={4}>
        Bulk Test Score Input
      </Typography>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Grade Class */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={gradeClasses}
              value={selectedGradeClass}
              onChange={(e, value) => {
                setSelectedGradeClass(value);
                setSelectedStudent(null);
              }}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.id === value.id
              }
              ListboxProps={{
                style: {
                  maxHeight: 9 * 48,
                  overflow: "auto",
                },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Grade Class" />
              )}
              disabled={loading}
            />
          </Grid>

          {/* Student */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={students}
              value={selectedStudent}
              onChange={(e, value) => setSelectedStudent(value)}
              getOptionLabel={(option) =>
                option?.full_name || ""
              }
              isOptionEqualToValue={(option, value) =>
                option.id === value.id
              }
              ListboxProps={{
                style: {
                  maxHeight: 9 * 48,
                  overflow: "auto",
                },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Student" />
              )}
              disabled={loading || !selectedGradeClass}
            />
          </Grid>

          {/* Subject */}
          <Grid item xs={12} md={4}>
            <Autocomplete
              options={subjects}
              value={selectedSubject}
              onChange={(e, value) => setSelectedSubject(value)}
              getOptionLabel={(option) => option?.name || ""}
              isOptionEqualToValue={(option, value) =>
                option.id === value.id
              }
              ListboxProps={{
                style: {
                  maxHeight: 9 * 48,
                  overflow: "auto",
                },
              }}
              renderInput={(params) => (
                <TextField {...params} label="Subject" />
              )}
              disabled={loading}
            />
          </Grid>

          {/* Period Fields */}
          {periods.map((period) => (
            <Grid item xs={12} sm={6} md={3} key={period}>
              <TextField
                label={period}
                type="number"
                fullWidth
                value={testScores[period]}
                onChange={(e) =>
                  handleScoreChange(period, e.target.value)
                }
                inputProps={{ min: 0, max: 100 }}
                disabled={loading}
              />
            </Grid>
          ))}

          {/* Submit */}
          <Grid item xs={12} textAlign="center">
            <Button
              variant="contained"
              type="submit"
              size="large"
              disabled={loading}
              sx={{ px: 6, py: 1.5 }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Save All Scores"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default BulkTestScorePage;
